// StatCardV3 — compact stat card with optional low-balance pulse badge.
//
// Props:
//   testId               – base data-testid; badge gets `${testId}-lowbalance-badge`
//   icon                 – lucide-react component
//   label                – small caption
//   value                – displayed number/string (already formatted by caller)
//   ctaLabel / ctaHref   – arrow-CTA under the value
//   badge                – { active: bool, tooltip: string, onClick?: () => void }
//   demo                 – when true, renders a subtle "Demo" tag under the card
//   ariaLabel            – aria-label override
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

export default function StatCardV3({
    testId,
    icon: Icon,
    label,
    value,
    ctaLabel,
    ctaHref,
    onCtaClick,
    badge,
    demo = false,
    demoLabel = "Demo",
    ariaLabel,
    delay = 0,
}) {
    const Chip = badge?.active ? (
        <button
            type="button"
            onClick={badge.onClick}
            title={badge.tooltip}
            aria-label={badge.tooltip}
            data-testid={`${testId}-lowbalance-badge`}
            className="relative inline-flex items-center justify-center h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: "#FAFAFA" }}
        >
            <span
                className="absolute inset-0 rounded-full animate-ping"
                style={{ backgroundColor: "#FAFAFA", opacity: 0.55 }}
            />
        </button>
    ) : null;

    const CTAInner = (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-[#FFFFFF] hover:text-[#CCCCCC] transition-colors">
            {ctaLabel}
            <ArrowUpRight className="w-3.5 h-3.5" />
        </span>
    );

    return (
        <motion.article
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay }}
            data-testid={testId}
            aria-label={ariaLabel || label}
            className="group rounded-2xl border p-5 transition-colors"
            style={{ backgroundColor: "#111111", borderColor: "#1A1A1A" }}
            onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#FFFFFF")}
            onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#1A1A1A")}
        >
            <div className="flex items-start justify-between">
                <span
                    className="h-9 w-9 rounded-xl grid place-items-center"
                    style={{ backgroundColor: "rgba(255, 255, 255,0.12)" }}
                >
                    <Icon className="h-4 w-4" style={{ color: "#FFFFFF" }} aria-hidden />
                </span>
                {Chip}
            </div>

            <p className="mt-4 text-3xl font-semibold tabular-nums tracking-tight text-[#FFFFFF]">
                {value}
            </p>
            <p className="mt-1 text-xs text-[#CCCCCC]">{label}</p>

            {ctaLabel && (
                <div className="mt-4">
                    {ctaHref ? (
                        <Link to={ctaHref} className="no-underline">
                            {CTAInner}
                        </Link>
                    ) : (
                        <button type="button" onClick={onCtaClick} className="cursor-pointer">
                            {CTAInner}
                        </button>
                    )}
                </div>
            )}

            {demo && (
                <p
                    className="mt-3 text-[10px] uppercase tracking-widest text-[#71717A] opacity-50"
                    style={{ filter: "grayscale(1)" }}
                >
                    🧪 {demoLabel}
                </p>
            )}
        </motion.article>
    );
}
